import { COORDINATE_QUANTUM_MM, BED_JOINT_GAP_MM, WallModel } from '@bw/core';
import type { BrickId, Sketch, SketchPoint, SketchShape } from '@bw/core';
import { ProjectDocument, APP_ID } from './project.js';
import type { Wall } from './project.js';
import { normaliseColour } from './materials.js';
import type {
  Envelope,
  JoinerySpec,
  Material,
  Opening,
  PlanSpan,
  RoofSpec,
  UnknownKeys,
  WallPlacement,
} from './types.js';
import { DEFAULT_PLACEMENT } from './types.js';

export const PROJECT_FORMAT = 'brickwall-project';
/**
 * Version 2 adds the roof and joinery settings (phase 2, M7). Version 3 adds
 * each wall's design span. All are additive, so an older file still opens —
 * see `SUPPORTED_VERSIONS`.
 *
 * The span earns a version of its own rather than riding along in 2, because
 * unlike the roof it changes how the GEOMETRY reads. A build that did not know
 * about it would take a corner-bonded wall's length from its brickwork, which
 * runs past the design line to tie the corner together, and would then draw a
 * plan whose outline no longer closes and a roof to match — quietly, and
 * looking for all the world as though that were the drawing. Better it says it
 * cannot open the file.
 */
export const PROJECT_VERSION = 3;
/** Every version this build can read. Refusing an older file loses work. */
export const SUPPORTED_VERSIONS: ReadonlySet<number> = new Set([1, 2, 3]);
export const PROJECT_EXTENSION = '.bwall';

const WALL_KNOWN_KEYS = new Set([
  'id', 'name', 'origin', 'rotationDeg', 'thicknessMm',
  'baseMaterialId', 'envelope', 'span', 'openings', 'bricks', 'sketch',
]);
const BRICK_KNOWN_KEYS = new Set(['id', 'kind', 'x', 'y', 'rotation', 'materialId']);
const ROOT_KNOWN_KEYS = new Set([
  'format', 'version', 'units', 'meta', 'settings', 'materials', 'walls', 'sheets', 'view',
  'roof', 'joinery',
]);

function unknownKeys(source: Record<string, unknown>, known: ReadonlySet<string>): UnknownKeys {
  const extra: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(source)) if (!known.has(key)) extra[key] = value;
  return extra;
}

/**
 * Serialise to the `.bwall` format (design document D2).
 *
 * The whole document is built in memory and returned as one string; the caller
 * writes it in a single operation. Never stream a partial document over a good
 * file.
 */
export function serialiseProject(project: ProjectDocument): string {
  const payload = {
    format: PROJECT_FORMAT,
    version: PROJECT_VERSION,
    units: 'mm',
    meta: { ...project.meta, app: APP_ID },
    settings: {
      perpendJointMm: project.activeWall.model.perpendGapMm,
      bedJointMm: BED_JOINT_GAP_MM,
      quantumMm: COORDINATE_QUANTUM_MM,
    },
    materials: project.materials,
    roof: project.roof,
    joinery: project.joinery,
    walls: project.walls.map((wall) => ({
      id: wall.id,
      name: wall.name,
      origin: wall.placement.origin,
      rotationDeg: wall.placement.rotationDeg,
      thicknessMm: wall.placement.thicknessMm,
      baseMaterialId: wall.baseMaterialId,
      envelope: wall.envelope,
      span: wall.span,
      openings: wall.openings,
      sketch: wall.model.sketch,
      perpendJointMm: wall.model.perpendGapMm,
      bricks: wall.model.bricks.map((brick) => ({
        id: brick.id,
        kind: brick.kind,
        x: brick.x,
        y: brick.y,
        rotation: brick.rotation,
        materialId: brick.materialId,
        ...(wall.brickExtra.get(brick.id) ?? {}),
      })),
      ...wall.extra,
    })),
    sheets: project.sheets,
    view: project.view,
    ...project.extra,
  };
  return `${JSON.stringify(payload, null, 2)}\n`;
}

export class ProjectFormatError extends Error {
  override readonly name = 'ProjectFormatError';
}

function fail(message: string): never {
  throw new ProjectFormatError(message);
}

function asRecord(value: unknown, message: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) fail(message);
  return value as Record<string, unknown>;
}

function parsePlacement(source: Record<string, unknown>): WallPlacement {
  const origin = source['origin'] as { x?: number; y?: number; z?: number } | undefined;
  const number = (value: unknown, fallback: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return {
    origin: {
      x: number(origin?.x, 0),
      y: number(origin?.y, 0),
      z: number(origin?.z, 0),
    },
    rotationDeg: number(source['rotationDeg'], DEFAULT_PLACEMENT.rotationDeg),
    thicknessMm: number(source['thicknessMm'], DEFAULT_PLACEMENT.thicknessMm),
  };
}

/** Reserved, schema-validated, never created in M1 (design document D1). */
/** Roof settings, every field defaulted so an older file needs none of them. */
function parseRoof(value: unknown): Partial<RoofSpec> {
  if (value === undefined || value === null) return {};
  const record = asRecord(value, 'Roof settings must be an object');
  const out: Record<string, unknown> = {};
  const style = record['style'];
  if (style === 'hip' || style === 'gable' || style === 'none') out['style'] = style;
  for (const key of ['pitchDeg', 'overhangMm'] as const) {
    const raw = record[key];
    if (typeof raw === 'number' && Number.isFinite(raw)) out[key] = raw;
  }
  if (typeof record['materialId'] === 'string') out['materialId'] = record['materialId'];
  return out as Partial<RoofSpec>;
}

function parseJoinery(value: unknown): Partial<JoinerySpec> {
  if (value === undefined || value === null) return {};
  const record = asRecord(value, 'Joinery settings must be an object');
  const out: Record<string, unknown> = {};
  for (const key of ['revealMm', 'frameMm', 'glassInsetMm'] as const) {
    const raw = record[key];
    if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) out[key] = raw;
  }
  if (typeof record['frameColour'] === 'string') out['frameColour'] = record['frameColour'];
  return out as Partial<JoinerySpec>;
}

function parseOpenings(value: unknown): Opening[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) fail('Wall openings must be a list');
  return value.map((entry, index) => {
    const record = asRecord(entry, `Opening ${index} must be an object`);
    const number = (key: string): number => {
      const raw = record[key];
      if (typeof raw !== 'number' || !Number.isFinite(raw)) {
        fail(`Opening ${index} has an invalid ${key}`);
      }
      return raw;
    };
    const type = record['type'];
    if (type !== 'window' && type !== 'door' && type !== 'other') {
      fail(`Opening ${index} has an unknown type`);
    }
    return {
      id: String(record['id'] ?? `o${index + 1}`),
      type,
      x: number('x'),
      y: number('y'),
      width: number('width'),
      height: number('height'),
      revealMm: typeof record['revealMm'] === 'number' ? record['revealMm'] : 0,
    };
  });
}

function parseEnvelope(value: unknown): Envelope | null {
  if (value === undefined || value === null) return null;
  const record = asRecord(value, 'Wall envelope must be an object');
  const number = (key: string): number => {
    const raw = record[key];
    if (typeof raw !== 'number' || !Number.isFinite(raw)) fail(`Wall envelope has an invalid ${key}`);
    return raw;
  };
  return { x: number('x'), y: number('y'), w: number('w'), h: number('h') };
}

/** Absent in a version 1 or 2 file, which is why it is null rather than required. */
function parseSpan(value: unknown): PlanSpan | null {
  if (value === undefined || value === null) return null;
  const record = asRecord(value, 'Wall span must be an object');
  const minX = record['minX'];
  const maxX = record['maxX'];
  if (typeof minX !== 'number' || !Number.isFinite(minX)) fail('Wall span has an invalid minX');
  if (typeof maxX !== 'number' || !Number.isFinite(maxX)) fail('Wall span has an invalid maxX');
  if (maxX <= minX) fail('A wall span must be longer than nothing');
  return { minX, maxX };
}

function parseSketch(value: unknown): Sketch | null {
  if (value === undefined || value === null) return null;
  const record = asRecord(value, 'Wall sketch must be an object');
  const points = (raw: unknown, where: string): SketchPoint[] => {
    if (!Array.isArray(raw)) fail(`${where} must be a list of points`);
    return raw.map((entry) => {
      const p = asRecord(entry, `${where} has a malformed point`);
      if (typeof p['x'] !== 'number' || typeof p['y'] !== 'number' || !Number.isFinite(p['x']) || !Number.isFinite(p['y'])) {
        fail(`${where} has a non-finite point`);
      }
      return { x: p['x'], y: p['y'] };
    });
  };
  const kind = (raw: unknown): 'wall' | 'construction' => (raw === 'construction' ? 'construction' : 'wall');
  const segments = Array.isArray(record['segments'])
    ? record['segments'].map((entry, index) => {
        const seg = asRecord(entry, `Sketch segment ${index} must be an object`);
        const [a, b] = points([seg['a'], seg['b']], `Sketch segment ${index}`) as [SketchPoint, SketchPoint];
        return { id: String(seg['id'] ?? `g${index + 1}`), kind: kind(seg['kind']), a, b };
      })
    : [];
  const shapes = Array.isArray(record['shapes'])
    ? record['shapes'].map((entry, index) => {
        const shape = asRecord(entry, `Sketch shape ${index} must be an object`);
        const role = shape['role'];
        // Not a version of its own, deliberately. A build that predates flint
        // refuses a file containing it, by name and loudly, which is what
        // should happen — and bumping the version to say so would make it
        // refuse every new file instead, including the ones it could read
        // perfectly well.
        if (role !== 'wall' && role !== 'window' && role !== 'door' && role !== 'flint') {
          fail(`Sketch shape ${index} has an unknown role`);
        }
        const parsed: SketchShape = {
          id: String(shape['id'] ?? `s${index + 1}`),
          role,
          kind: kind(shape['kind']),
          points: points(shape['points'], `Sketch shape ${index}`),
        };
        return parsed;
      })
    : [];
  return { segments, shapes };
}

function parseMaterials(value: unknown): Material[] {
  if (!Array.isArray(value) || value.length === 0) fail('Project does not contain a valid material list');
  return value.map((entry, index) => {
    const record = asRecord(entry, `Material ${index} must be an object`);
    const id = record['id'];
    if (typeof id !== 'string' || id === '') fail(`Material ${index} has no id`);
    return {
      id,
      name: typeof record['name'] === 'string' ? record['name'] : id,
      colour: normaliseColour(record['colour']),
      texture: typeof record['texture'] === 'string' ? record['texture'] : null,
      roughness: typeof record['roughness'] === 'number' ? record['roughness'] : null,
    };
  });
}

/** Parse a `.bwall` document. Throws rather than loading a corrupt project. */
export function deserialiseProject(text: string): ProjectDocument {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    fail('This file is not valid JSON');
  }
  const data = asRecord(raw, 'This is not a Brickwall project');
  if (data['format'] !== PROJECT_FORMAT) fail('This is not a Brickwall project');
  if (typeof data['version'] !== 'number' || !SUPPORTED_VERSIONS.has(data['version'])) {
    fail(`Unsupported project version: ${String(data['version'])}`);
  }
  const walls = data['walls'];
  if (!Array.isArray(walls) || walls.length === 0) {
    fail('Project does not contain a valid wall list');
  }

  const meta = asRecord(data['meta'] ?? {}, 'Project metadata must be an object');
  const settings = asRecord(data['settings'] ?? {}, 'Project settings must be an object');
  const defaultGap =
    typeof settings['perpendJointMm'] === 'number' ? settings['perpendJointMm'] : 10;

  const project = new ProjectDocument(typeof meta['name'] === 'string' ? meta['name'] : 'Untitled');
  project.setMaterials(parseMaterials(data['materials']));
  // Absent in a version 1 file, which is why both are defaulted rather than
  // required: an older project opens with a default roof, not an error.
  project.setRoof(parseRoof(data['roof']));
  project.setJoinery(parseJoinery(data['joinery']));
  // The constructor seeds one empty wall; replace it with what the file holds.
  for (const wall of [...project.walls]) project.removeWall(wall.id);
  const seeded = project.walls[0];

  const built: Wall[] = walls.map((entry, index) => {
    const record = asRecord(entry, `Wall ${index} must be an object`);
    const bricks = record['bricks'];
    if (!Array.isArray(bricks)) fail(`Wall ${index} does not contain a valid brick list`);
    const gap = typeof record['perpendJointMm'] === 'number' ? record['perpendJointMm'] : defaultGap;
    const model = new WallModel([], gap);
    const brickExtra = new Map<BrickId, UnknownKeys>();
    model.restoreRecords(
      bricks.map((brick) => {
        const brickRecord = asRecord(brick, 'Each brick record must be an object');
        const extra = unknownKeys(brickRecord, BRICK_KNOWN_KEYS);
        if (Object.keys(extra).length > 0 && typeof brickRecord['id'] === 'number') {
          brickExtra.set(brickRecord['id'], extra);
        }
        return brickRecord;
      }),
      gap,
    );
    const sketch = parseSketch(record['sketch']);
    if (sketch !== null) model.setSketch(sketch);
    return {
      id: typeof record['id'] === 'string' ? record['id'] : `w${index + 1}`,
      name: typeof record['name'] === 'string' ? record['name'] : `Wall ${index + 1}`,
      placement: parsePlacement(record),
      baseMaterialId:
        typeof record['baseMaterialId'] === 'string'
          ? record['baseMaterialId']
          : (project.materials[0] as Material).id,
      envelope: parseEnvelope(record['envelope']),
      span: parseSpan(record['span']),
      openings: parseOpenings(record['openings']),
      model,
      extra: unknownKeys(record, new Set([...WALL_KNOWN_KEYS, 'perpendJointMm'])),
      brickExtra,
    };
  });

  // A brick naming a material nobody defined would otherwise paint in
  // whatever happens to be first, silently. Repair it to the wall's base
  // material and say so, rather than refusing to open the file over a colour.
  const knownMaterials = new Set(project.materials.map((material) => material.id));
  const warnings: string[] = [];
  for (const wall of built) {
    if (!knownMaterials.has(wall.baseMaterialId)) {
      warnings.push(`Wall "${wall.name}" named an unknown base material; used the first instead.`);
      wall.baseMaterialId = (project.materials[0] as Material).id;
    }
    const unknown = new Set<string>();
    for (const brick of wall.model.bricks) {
      if (!knownMaterials.has(brick.materialId)) unknown.add(brick.materialId);
    }
    if (unknown.size > 0) {
      let repaired = 0;
      for (const brick of [...wall.model.bricks]) {
        if (!knownMaterials.has(brick.materialId)) {
          wall.model.setMaterial(brick.id, wall.baseMaterialId);
          repaired += 1;
        }
      }
      warnings.push(
        `Wall "${wall.name}": ${repaired} brick${repaired === 1 ? '' : 's'} referred to a material ` +
          `that is not in this project (${[...unknown].join(', ')}); repainted in the wall's base material.`,
      );
    }
  }
  for (const wall of built) project.adoptWall(wall);
  project.loadWarnings = warnings;
  if (seeded !== undefined) project.removeWall(seeded.id);

  const view = data['view'];
  if (typeof view === 'object' && view !== null) {
    const record = view as Record<string, unknown>;
    if (
      typeof record['wallId'] === 'string' &&
      typeof record['scale'] === 'number' &&
      typeof record['originX'] === 'number' &&
      typeof record['originY'] === 'number'
    ) {
      project.view = {
        wallId: record['wallId'],
        scale: record['scale'],
        originX: record['originX'],
        originY: record['originY'],
      };
      project.setActiveWall(record['wallId']);
    }
  }
  if (Array.isArray(data['sheets'])) project.sheets = data['sheets'] as UnknownKeys[];
  project.extra = unknownKeys(data, ROOT_KNOWN_KEYS);
  // Last, so the mutations above cannot stamp a new modified time on a file
  // that has merely been opened.
  project.restoreMeta({
    name: typeof meta['name'] === 'string' ? meta['name'] : project.name,
    created: typeof meta['created'] === 'string' ? meta['created'] : new Date().toISOString(),
    modified: typeof meta['modified'] === 'string' ? meta['modified'] : new Date().toISOString(),
    app: typeof meta['app'] === 'string' ? meta['app'] : APP_ID,
  });
  return project;
}
